<template>
  <div class="settings-section">
    <h2 class="section-title">{{ $t('settings.taxSettings', 'Tax Settings') }}</h2>
    <p class="section-description">{{ $t('settings.taxSettingsDesc', 'Configure GST and tax regime for your business') }}</p>

    <div class="settings-form">
      <!-- Tax Regime -->
      <div class="form-field">
        <label class="form-label">
          {{ $t('settings.taxRegime', 'Tax Regime') }}
          <span class="required">*</span>
        </label>
        <div class="radio-group">
          <label class="radio-option" :class="{ selected: formData.tax_regime === 'regular' }">
            <input
              type="radio"
              v-model="formData.tax_regime"
              value="regular"
            />
            <div class="radio-content">
              <span class="radio-title">{{ $t('settings.regularGST', 'Regular GST') }}</span>
              <span class="radio-description">{{ $t('settings.regularGSTDesc', 'Full GST compliance with input tax credit') }}</span>
            </div>
          </label>
          <label class="radio-option" :class="{ selected: formData.tax_regime === 'composition' }">
            <input
              type="radio"
              v-model="formData.tax_regime"
              value="composition"
            />
            <div class="radio-content">
              <span class="radio-title">{{ $t('settings.compositionScheme', 'Composition Scheme') }}</span>
              <span class="radio-description">{{ $t('settings.compositionSchemeDesc', 'Simplified tax at fixed rate, no input credit') }}</span>
            </div>
          </label>
        </div>
      </div>

      <!-- GSTIN -->
      <div class="form-field">
        <IntercomInput
          v-model="formData.gstin"
          :label="$t('settings.gstin', 'GSTIN')"
          :placeholder="$t('settings.gstinPlaceholder', '22AAAAA0000A1Z5')"
          :error="gstinError"
          maxlength="15"
        />
        <p class="field-hint">{{ $t('settings.gstinHint', '15-character GST Identification Number') }}</p>
      </div>

      <!-- State Code -->
      <div class="form-field">
        <IntercomSelect
          v-model="formData.state_code"
          :label="$t('settings.stateCode', 'State Code') + ' *'"
          :options="indianStateOptions"
          placeholder="Select state..."
          filterable
          hint="Required for GST invoicing"
        />
      </div>
    </div>

    <!-- Save Footer -->
    <div class="save-footer" v-if="isDirty || saveStatus">
      <button
        v-if="isDirty"
        class="btn-discard"
        @click="handleDiscard"
      >
        {{ $t('settings.discard', 'Discard') }}
      </button>
      <IntercomButton
        variant="primary"
        :class="{ saved: saveStatus === 'saved' }"
        :disabled="!isDirty && saveStatus !== 'saved'"
        @click="handleSave"
      >
        {{ isSaving ? $t('common.saving', 'Saving...') : saveStatus === 'saved' ? $t('common.saved', 'Saved') + ' ✓' : $t('settings.saveChanges', 'Save Changes') }}
      </IntercomButton>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useBilling } from '../../composables/useBilling.js'
import { IntercomInput, IntercomButton, IntercomSelect } from '@shared/components/intercom'

const { t } = useI18n()
const toast = useToast()
const { getBusiness, updateBusiness } = useBilling()

const originalData = ref({})
const fullBusinessData = ref({}) // Store full business data
const isSaving = ref(false)
const saveStatus = ref(null)
const gstinError = ref('')

// GSTIN validation regex (15 characters)
const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/

// Indian states with GST codes
const indianStates = [
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman & Diu' },
  { code: '26', name: 'Dadra & Nagar Haveli' },
  { code: '27', name: 'Maharashtra' },
  { code: '28', name: 'Andhra Pradesh (Old)' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman & Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh (New)' },
  { code: '38', name: 'Ladakh' },
]

const indianStateOptions = indianStates.map(s => ({
  value: s.code,
  label: `${s.code} - ${s.name}`,
}))

const formData = reactive({
  tax_regime: 'regular',
  gstin: '',
  state_code: '33', // Default to Tamil Nadu
})

// Track dirty state
const isDirty = computed(() => {
  return JSON.stringify(formData) !== JSON.stringify(originalData.value)
})

// Clear errors when user types
watch(() => formData.gstin, () => {
  gstinError.value = ''
})

const validateGstin = () => {
  if (formData.gstin && !gstinRegex.test(formData.gstin.toUpperCase())) {
    gstinError.value = t('errors.invalidGstin', 'Please enter a valid 15-character GSTIN')
    return false
  }
  return true
}

const handleSave = async () => {
  if (!isDirty.value) return

  // Validate GSTIN if provided
  if (!validateGstin()) {
    return
  }

  // Validate state code
  if (!formData.state_code) {
    toast.warning(t('settings.stateCodeRequired', 'State code is required'))
    return
  }

  isSaving.value = true
  try {
    // Merge form data with full business data to preserve other settings
    const updateData = {
      ...fullBusinessData.value,
      ...formData,
      gstin: formData.gstin.toUpperCase(), // Always uppercase GSTIN
    }
    await updateBusiness(updateData)
    // Update original data to match current
    originalData.value = { ...formData }
    saveStatus.value = 'saved'
    toast.success(t('settings.taxUpdated', 'Tax settings updated successfully'))
    setTimeout(() => {
      saveStatus.value = null
    }, 2000)
  } catch (error) {
    console.error('Failed to save tax settings:', error)
    toast.error(t('settings.updateFailed', 'Failed to update tax settings'))
  } finally {
    isSaving.value = false
  }
}

const handleDiscard = () => {
  Object.assign(formData, originalData.value)
  gstinError.value = ''
}

const fetchBusiness = async () => {
  try {
    const business = await getBusiness()
    fullBusinessData.value = { ...business }

    // Populate form with business data
    formData.tax_regime = business.tax_regime || 'regular'
    formData.gstin = business.gstin || ''
    formData.state_code = business.state_code || '33' // Default to Tamil Nadu

    // Store original for dirty checking
    originalData.value = { ...formData }
  } catch (error) {
    console.error('Failed to fetch tax settings:', error)
    toast.error(t('settings.fetchFailed', 'Failed to load tax settings'))
  }
}

onMounted(() => {
  fetchBusiness()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.settings-section {
  padding: var(--space-6);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1) 0;
}

.section-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-6) 0;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.required {
  color: var(--color-red);
  margin-left: var(--space-1);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin: 0;
}

/* Radio Group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border: 2px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.radio-option:hover {
  border-color: var(--color-gray-300);
}

.radio-option.selected {
  background: var(--color-blue-light);
  border-color: var(--color-blue);
}

.radio-option input[type="radio"] {
  margin-top: var(--space-1);
  accent-color: var(--color-blue);
}

.radio-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.radio-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.radio-description {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

/* Save Footer */
.save-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  margin: var(--space-6) calc(-1 * var(--space-6)) calc(-1 * var(--space-6));
  background: var(--color-white);
  border-top: 1px solid var(--color-gray-200);
}

.btn-discard {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-discard:hover {
  background: var(--color-gray-50);
  border-color: var(--color-gray-400);
}

.saved {
  background: var(--color-success) !important;
}
</style>
